import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import * as fromLookup from '../../../../lookups';
import { LoggingService } from '../../../../shared/services/logging';
import { LocaleEnum } from '../../../expense-enums';
import * as fromCity from '../../../store/city';

@Component({
    selector: 'ef-expense-detail-city-state-zip-container',
    templateUrl: './expense-detail-city-state-zip-container.component.html',
    styleUrls: ['./expense-detail-city-state-zip-container.component.scss'],
    standalone: false
})
export class ExpenseDetailCityStateZipContainerComponent
    implements OnInit, OnChanges
{
    public cities$: Observable<fromCity.City[]>;
    public states$: Observable<fromLookup.IState[]>;
    public countries$: Observable<fromLookup.ILocation[]>;
    public areas$: Observable<fromLookup.ILocation[]>;
    public locations$: Observable<fromLookup.ILocation[]>;
    @Input() public locale: LocaleEnum;
    @Input() public selectedCity: fromCity.City;
    @Input() public selectedState: fromLookup.IState;
    @Input() public selectedPostalCode: string;
    @Input() public selectedCountryArea: fromLookup.ILocation;
    @Input() public selectedLocation: fromLookup.ILocation;
    @Input() showAddressDialog: boolean;
    @Output() showAddressDialogChange = new EventEmitter<boolean>();
    @Output() SetAddressData = new EventEmitter<any>();
    private SetPostalCode = new ReplaySubject<string>(1);

    constructor(private log: LoggingService, private store: Store) {}

    ngOnInit(): void {
        this.cities$ = this.store.pipe(select(fromCity.selectAll));
        this.states$ = combineLatest([
            this.SetPostalCode.pipe(
                startWith(null as string),
                tap(postalCode =>
                    this.log.verbose({
                        type: 'ExpenseDetailCityStateZipContainerComponent:states$:postalCode',
                        postalCode
                    })
                )
            ),
            this.store.pipe(
                select(fromCity.selectAll),
                startWith(null as fromCity.City[]),
                tap(cities =>
                    this.log.verbose({
                        type: 'ExpenseDetailCityStateZipContainerComponent:states$:cities',
                        cities
                    })
                )
            ),
            this.store.pipe(
                select(fromLookup.getStates),
                tap(states =>
                    this.log.verbose({
                        type: 'ExpenseDetailCityStateZipContainerComponent:states$:states',
                        states
                    })
                )
            )
        ]).pipe(
            tap(([postalCode, cities, states]) =>
                this.log.verbose({
                    type: 'ExpenseDetailCityStateZipContainerComponent:states$:postalCode, cities, states',
                    states,
                    cities,
                    postalCode
                })
            ),
            map(([postalCode, cities, states]) =>
                !!postalCode && cities && cities.length
                    ? cities.map(city => ({ State: city.State }))
                    : states
            )
        );

        this.countries$ = this.store.pipe(select(fromLookup.getCountries));
        this.areas$ = this.store.pipe(select(fromLookup.getAreas));
        this.locations$ = this.store.pipe(select(fromLookup.getLocations));
    }
    ngOnChanges(changes: SimpleChanges): void {
        this.log.verbose({
            type: 'ExpenseDetailCityStateZipContainerComponent:ngOnChanges',
            changes
        });
        if (
            changes &&
            changes.selectedPostalCode &&
            changes.selectedPostalCode.currentValue
        ) {
            this.store.dispatch(
                fromCity.getCitys({
                    ZipCode: changes.selectedPostalCode.currentValue
                })
            );
        } else {
            if (
                changes &&
                changes.selectedState &&
                changes.selectedState.currentValue
            ) {
                this.store.dispatch(
                    fromCity.getCitys({
                        State: changes.selectedState.currentValue.State
                    })
                );
            }
        }
    }
    onSelectState(state: fromLookup.IState) {
        if (this.selectedState !== state) {
            this.selectedState = state;
            this.log.verbose({
                type: 'ExpenseDetailCityStateZipContainerComponent:onSelectState',
                state,
                locale: this.locale
            });
            if (this.locale === LocaleEnum.Domestic) {
                this.store.dispatch(fromCity.getCitys({ State: state.State }));
            } else {
                this.store.dispatch(
                    new fromLookup.LoadLocations({
                        locale: this.locale,
                        countryArea: state.State
                    })
                );
            }
        }
    }
    onSelectPostalCode(postalCode: string) {
        if (this.selectedPostalCode !== postalCode) {
            this.selectedPostalCode = postalCode;
            this.log.verbose({
                type: 'ExpenseDetailCityStateZipContainerComponent:onSelectPostalCode',
                postalCode,
                locale: this.locale
            });
            this.store.dispatch(fromCity.loadCitys({ citys: [] }));
            this.store.dispatch(fromCity.getCitys({ ZipCode: postalCode }));
            this.SetPostalCode.next(postalCode);
        }
    }
    onSelectCountry(location: fromLookup.ILocation) {
        if (this.selectedCountryArea !== location) {
            this.selectedCountryArea = location;
            this.log.verbose({
                type: 'ExpenseDetailCityStateZipContainerComponent:onSelectCountry',
                location,
                locale: this.locale
            });
            this.store.dispatch(
                new fromLookup.LoadLocations({
                    locale: this.locale,
                    countryArea: location.CountryArea
                })
            );
        }
    }
    onClose($event) {
        this.log.verbose({
            type: 'ExpenseDetailCityStateZipContainerComponent:onClose',
            $event
        });
        if (!!$event) {
            this.SetAddressData.next($event);
        }
        this.showAddressDialogChange.next(false);
    }
}
